import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { formatApiErrorMessage } from '../api/apiErrorMessage'
import {
  copySavedOrderBlankText,
  downloadSavedOrderBlankPdf,
  getSavedOrderBlank,
  listSavedOrderBlanks,
  type SavedOrderBlankDto,
} from '../api/orderBlankHistory'
import { useAuth } from '../auth/AuthProvider'
import { useCart } from '../cart/CartProvider'
import { DataTable } from '../components/DataTable'
import { MarketEmptyState } from '../components/MarketEmptyState'
import { buildOrderBlankFilename } from '../utils/orderBlankFilename'
import { formatUnitLabel } from '../utils/formatUnitLabel'

const formatRub = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) {
    return '—'
  }
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 2,
  }).format(value)
}

const formatWhen = (iso: string) => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return '—'
  }
  return date.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const OrderBlankHistoryPanel = () => {
  const navigate = useNavigate()
  const { session, organizationName } = useAuth()
  const { restoreExternalSupplierLines } = useCart()
  const organizationId = session?.organizationId ?? 0
  const [items, setItems] = useState<SavedOrderBlankDto[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [detail, setDetail] = useState<SavedOrderBlankDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [detailLoading, setDetailLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<'pdf' | 'text' | 'reuse' | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const loadList = useCallback(async () => {
    if (!organizationId) {
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await listSavedOrderBlanks(organizationId)
      setItems(data)
    } catch (err) {
      setError(formatApiErrorMessage(err, 'Не удалось загрузить историю бланков'))
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    void loadList()
  }, [loadList])

  useEffect(() => {
    if (selectedId == null || !organizationId) {
      setDetail(null)
      return
    }
    let cancelled = false
    void (async () => {
      setDetailLoading(true)
      setMessage(null)
      try {
        const data = await getSavedOrderBlank(organizationId, selectedId)
        if (!cancelled) {
          setDetail(data)
        }
      } catch (err) {
        if (!cancelled) {
          setDetail(null)
          setError(formatApiErrorMessage(err, 'Не удалось открыть бланк'))
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [organizationId, selectedId])

  const handlePdf = async () => {
    if (selectedId == null) {
      return
    }
    setActionLoading('pdf')
    setMessage(null)
    try {
      const blob = await downloadSavedOrderBlankPdf(organizationId, selectedId)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = buildOrderBlankFilename(organizationName ?? 'restoran', organizationId)
      anchor.click()
      URL.revokeObjectURL(url)
      setMessage('PDF скачан')
    } catch (err) {
      setMessage(formatApiErrorMessage(err, 'Не удалось скачать PDF'))
    } finally {
      setActionLoading(null)
    }
  }

  const handleCopy = async () => {
    if (selectedId == null) {
      return
    }
    setActionLoading('text')
    setMessage(null)
    try {
      await copySavedOrderBlankText(organizationId, selectedId)
      setMessage('Текст скопирован в буфер обмена')
    } catch (err) {
      setMessage(formatApiErrorMessage(err, 'Не удалось скопировать текст'))
    } finally {
      setActionLoading(null)
    }
  }

  const handleReuse = () => {
    if (detail == null || detail.private_supplier_id == null) {
      setMessage('Нельзя переиспользовать: нет поставщика в снимке')
      return
    }
    setActionLoading('reuse')
    setMessage(null)
    try {
      restoreExternalSupplierLines({
        privateSupplierId: detail.private_supplier_id,
        supplierName: detail.supplier_name,
        lines: detail.lines
          .filter((line) => line.catalog_item_id != null && line.catalog_item_id > 0)
          .map((line) => ({
            catalogItemId: line.catalog_item_id as number,
            itemName: line.name,
            brand: line.brand,
            unit: line.unit ?? 'кг',
            price: line.unit_price != null ? String(line.unit_price) : null,
            quantity: Number(line.quantity),
            note: line.note ?? '',
          })),
      })
      navigate('/cart')
    } catch (err) {
      setMessage(formatApiErrorMessage(err, 'Не удалось открыть заявку в черновике'))
    } finally {
      setActionLoading(null)
    }
  }

  if (loading) {
    return <p className="echat-muted">Загрузка истории…</p>
  }

  if (error && items.length === 0) {
    return (
      <p className="echat-error" role="alert">
        {error}
      </p>
    )
  }

  if (items.length === 0) {
    return (
      <MarketEmptyState
        title="История пока пуста"
        description="В черновике нажмите «Сохранить заявку» — она появится здесь, и её можно будет снова открыть."
      />
    )
  }

  return (
    <div className="market-orders-split market-order-blank-history">
      <aside className="market-orders-split__list">
        <p className="echat-muted market-request-history-lead">
          Сохранённые заявки. Можно открыть снова в черновике или скачать бланк.
        </p>
        <ul className="market-order-blank-history__list" role="list">
          {items.map((item) => {
            const active = selectedId === item.id
            return (
              <li key={item.id}>
                <button
                  type="button"
                  className={
                    active
                      ? 'market-order-blank-history__item is-active'
                      : 'market-order-blank-history__item'
                  }
                  onClick={() => setSelectedId(item.id)}
                >
                  <span className="market-order-blank-history__item-title">{item.supplier_name}</span>
                  <span className="echat-muted market-order-blank-history__item-meta">
                    {formatWhen(item.created_at)} · {item.line_count} поз.
                    {item.total_amount != null ? ` · ${formatRub(item.total_amount)}` : ''}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      </aside>
      <main className="market-orders-split__detail">
        {selectedId == null ? (
          <p className="echat-muted">Выберите бланк слева, чтобы посмотреть позиции.</p>
        ) : detailLoading ? (
          <p className="echat-muted">Загрузка бланка…</p>
        ) : detail == null ? (
          <p className="echat-muted">Не удалось открыть бланк.</p>
        ) : (
          <>
            <div className="market-order-blank-history__detail-header">
              <div>
                <h2 className="echat-panel__subtitle">{detail.supplier_name}</h2>
                <p className="echat-muted">
                  {formatWhen(detail.created_at)}
                  {detail.supplier_inn ? ` · ИНН ${detail.supplier_inn}` : ''}
                </p>
              </div>
              <div className="market-cart-external-actions__buttons">
                <button
                  type="button"
                  className="echat-btn echat-btn--primary echat-btn--sm"
                  disabled={actionLoading != null || detail.private_supplier_id == null}
                  onClick={handleReuse}
                >
                  {actionLoading === 'reuse' ? 'Открываем…' : 'В черновик'}
                </button>
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost echat-btn--sm"
                  disabled={actionLoading != null}
                  onClick={() => void handlePdf()}
                >
                  {actionLoading === 'pdf' ? 'Формируем…' : 'Скачать PDF'}
                </button>
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost echat-btn--sm"
                  disabled={actionLoading != null}
                  onClick={() => void handleCopy()}
                >
                  {actionLoading === 'text' ? 'Копируем…' : 'Скопировать текст'}
                </button>
              </div>
            </div>
            {message ? <p className="market-cart-external-actions__message">{message}</p> : null}
            <DataTable
              wrapClassName="market-table-wrap--cards"
              className="market-table--cards"
              caption="Позиции бланка"
            >
              <thead>
                <tr>
                  <th>Позиция</th>
                  <th>Бренд</th>
                  <th>Кол-во</th>
                  <th>Ед.</th>
                  <th>Цена</th>
                  <th>Сумма</th>
                </tr>
              </thead>
              <tbody>
                {detail.lines.map((line) => (
                  <tr key={line.id}>
                    <td className="market-table__cell--primary" data-label="Позиция">
                      {line.name}
                      {line.note ? <div className="echat-muted">{line.note}</div> : null}
                    </td>
                    <td data-label="Бренд">{line.brand ?? '—'}</td>
                    <td data-label="Кол-во">{line.quantity}</td>
                    <td data-label="Ед.">{formatUnitLabel(line.unit)}</td>
                    <td data-label="Цена">{formatRub(line.unit_price)}</td>
                    <td data-label="Сумма">{formatRub(line.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
            {detail.total_amount != null ? (
              <p className="market-order-blank-history__total">
                Итого: <strong>{formatRub(detail.total_amount)}</strong>
              </p>
            ) : null}
          </>
        )}
      </main>
    </div>
  )
}
